/**
 * WD-T1 (worker-dash spec v1.2 item 1): governed per-personnel PPE check-off
 * list for JHAs. Mirrors server/src/features/jha/jhaPpe.ts — ids and point
 * ids MUST stay in sync (guarded by jhaPpeGear.parity.test.ts, which reads
 * the server module source and compares). Labels reuse the PPE inspection
 * page checklists so a JHA check-off describes the same physical checks.
 */

export interface JhaPpeGearPoint {
  id: string;
  label: string;
}

export interface JhaPpeGearDef {
  id: string;
  label: string;
  required: boolean;
  points: JhaPpeGearPoint[];
}

export const JHA_PPE_GEAR: JhaPpeGearDef[] = [
  {
    id: 'harness',
    label: 'Full Body Harness',
    required: true,
    points: [
      { id: 'harness_webbing', label: 'Webbing - No cuts, burns, or abrasions' },
      { id: 'harness_stitching', label: 'Stitching - All intact, no broken threads' },
      { id: 'harness_d_ring', label: 'D-Ring - No cracks, bends, or corrosion' },
      { id: 'harness_buckles', label: 'Buckles - Engage properly, no damage' },
      { id: 'harness_grommets', label: 'Grommets - Secure, no tears' },
      { id: 'harness_labels', label: 'Labels - Legible, not missing' },
    ],
  },
  {
    id: 'y-lanyard',
    label: 'Y-Lanyard',
    required: true,
    points: [
      { id: 'lanyard_webbing', label: 'Webbing - No cuts, fraying, or damage' },
      { id: 'lanyard_shock', label: 'Shock Absorber - Not deployed, pack intact' },
      { id: 'lanyard_hooks', label: 'Snap Hooks - Gate closes properly, no cracks' },
      { id: 'lanyard_stitching', label: 'Stitching - All intact at connection points' },
      { id: 'lanyard_corrosion', label: 'Hardware - No rust or corrosion' },
    ],
  },
  {
    id: 'srl-lanyard',
    label: 'SRL Lanyard (Self-Retracting)',
    required: false,
    points: [
      { id: 'srl_housing', label: 'Housing - No cracks, dents, or damage' },
      { id: 'srl_line', label: 'Lifeline (cable/webbing) - No kinks, fraying, or damage' },
      { id: 'srl_retraction', label: 'Retraction - Line retracts smoothly under tension' },
      { id: 'srl_braking', label: 'Braking - Locks when pulled sharply' },
      { id: 'srl_hook', label: 'Snap hook - Gate closes, double-locking works' },
      { id: 'srl_indicator', label: 'Fall indicator - Not deployed/activated' },
    ],
  },
  {
    id: 'spreader-bar',
    label: 'Spreader Bar',
    required: false,
    points: [
      { id: 'spreader_structure', label: 'Main Structure - No bends, cracks, or deformation' },
      { id: 'spreader_welds', label: 'Welds - No cracks or separation' },
      { id: 'spreader_shackles', label: 'Shackle Points - Pins secure, threads good' },
      { id: 'spreader_markings', label: 'Load Markings - WLL clearly visible' },
    ],
  },
  {
    id: 'safety-climb-device',
    label: 'Safety Climb Device',
    required: false,
    points: [
      { id: 'lifeline_rope', label: 'Rope - No cuts, abrasions, glazing, or soft spots' },
      { id: 'lifeline_termination', label: 'Termination/Splice - Intact, no pulled strands' },
      { id: 'lifeline_thimble', label: 'Thimble/Eye - Secure, no deformation' },
      { id: 'ropegrab_cam', label: 'Cam/Locking mechanism - Engages and releases properly' },
    ],
  },
  {
    id: 'carabiners',
    label: 'Carabiners',
    required: true,
    points: [
      { id: 'carabiner_body', label: 'Body - No cracks, distortion, or sharp edges' },
      { id: 'carabiner_gate', label: 'Gate - Opens and closes smoothly' },
      { id: 'carabiner_lock', label: 'Locking mechanism - Auto-locks, manual unlocks properly' },
      { id: 'carabiner_marking', label: 'Markings - Load rating visible' },
    ],
  },
  {
    id: 'hard-hat',
    label: 'Hard Hat',
    required: true,
    points: [
      { id: 'helmet_shell', label: 'Shell - No cracks, dents, or UV damage' },
      { id: 'helmet_suspension', label: 'Suspension - All straps intact, no wear' },
      { id: 'helmet_chinstrap', label: 'Chin Strap - Present and functional' },
    ],
  },
];

export interface JhaPpeCheck {
  gearId: string;
  /**
   * v1.5 (Brian 2026-10-03): the SPECIFIC tracked item this check-off covers
   * (model + serial line item). One check-off = ONE item — never fanned out.
   * Absent = legacy type-level row (auto-binds server-side when unambiguous).
   */
  assetId?: number;
  notUsed?: boolean;
  results?: Array<{ pointId: string; status: 'PASS' | 'FAIL' }>;
}

/** One checklist LINE ITEM (v1.5 §1-3): a physical piece or an untracked line. */
export interface PpeLineItem {
  assetId: number | null;
  gearId: string;
  name?: string | null;
  model?: string | null;
  serialNumber?: string | null;
  /** UAT-008: carried from the tracked asset so the UI can label/tag-out DAMAGED gear. */
  status?: string | null;
}

/** UAT-008: statuses that mean the piece is removed from service (tagged out). */
export const TAGGED_OUT_ASSET_STATUSES = new Set(['DAMAGED', 'MAINTENANCE', 'RETIRED', 'EXPIRED']);

/** Human identity of a line item: "Miller H-500 (SN 12345)" / the name fallback. */
export function itemIdentity(item: {
  assetId?: number | null; name?: string | null; model?: string | null; serialNumber?: string | null;
}): string {
  const parts: string[] = [];
  if (item.model) parts.push(item.model);
  if (item.serialNumber) parts.push(`SN ${item.serialNumber}`);
  const core = parts.length > 0 ? parts.join(' ') : (item.name || '').trim();
  if (core && item.name && !core.includes(item.name) && parts.length > 0) return `${item.name} (${core})`;
  return core || (item.assetId != null ? `item #${item.assetId}` : 'untracked gear');
}

/** A crew-member row as the JHA form holds it (subset the PPE logic needs). */
export interface JhaCrewMemberLike {
  id: string;
  userId?: number | null;
  name: string;
  signature?: string;
  ppeChecks?: JhaPpeCheck[];
}

const HOW_TO_FIX =
  'Every listed person must individually check off each PPE inspection item for their own gear and sign for themselves before the JHA can be submitted — complete that person’s checklist and signature in the Project Personnel step. One person cannot check off or sign for another.';

function rowComplete(check: JhaPpeCheck | undefined, gear: JhaPpeGearDef): boolean {
  if (!check || check.notUsed === true) return false;
  const done = new Set((check.results || []).filter((r) => r.status === 'PASS' || r.status === 'FAIL').map((r) => r.pointId));
  return gear.points.every((p) => done.has(p.id));
}

function checkedCount(check: JhaPpeCheck | undefined): number {
  return check && Array.isArray(check.results) ? check.results.length : 0;
}

/**
 * v1.5 client mirror of the server's shared binder (bindPpeChecks in
 * server/src/features/jha/jhaPpe.ts — same rules and same messages so the
 * pre-submit UX matches the authoritative server verdict). itemsByUserId
 * (optional) = the members' resolved line items from GET /jha/ppe-items;
 * without it the asset-dependent rules are skipped (the server enforces
 * those authoritatively on submit).
 */
/**
 * MED-1 (Luna delta-7): shared why+how message for the gear-list-unavailable
 * submit gate (every failure message tells the user WHY + HOW to fix — Brian's
 * UX standard 2026-09-20).
 */
export const PPE_ITEMS_UNAVAILABLE_BLOCKED =
  'The gear list failed to load, so these check-offs cannot be verified against their specific items (model + serial). Reload the page to load the gear list, then complete the check-offs and try again.';

/** Luna delta8-2: same gate while the request is still IN FLIGHT (slow network). */
export const PPE_ITEMS_STILL_LOADING =
  'The gear list is still loading, so these check-offs cannot be verified against their specific items (model + serial). Wait a moment for the list to load, then complete the check-offs and try again.';

export function collectPpeSignoffIssues(
  crewMembers: JhaCrewMemberLike[],
  itemsByUserId?: Map<number, PpeLineItem[]> | Record<number, PpeLineItem[]>,
): string[] {
  const issues: string[] = [];
  const lookup = (userId: number): PpeLineItem[] | undefined => {
    if (!itemsByUserId) return undefined;
    const hit = itemsByUserId instanceof Map ? itemsByUserId.get(userId) : itemsByUserId[userId];
    if (!Array.isArray(hit)) return itemsByUserId instanceof Map && itemsByUserId.has(userId) ? [] : undefined;
    // UAT-009 (server mirror): a member with NO tracked gear at all stays in
    // pure type-level mode — the per-type required minimum keeps applying.
    return hit.some((i) => i.assetId != null) ? hit : undefined;
  };
  const seenUsers = new Map<number, string>();
  for (const m of crewMembers || []) {
    if (!m || typeof m !== 'object' || typeof m.userId !== 'number') continue;
    const name = m.name?.trim() || `Crew member ${m.id}`;
    // Sol HIGH-4 (client mirror of the server roster gate): ONE person, ONE
    // entry — a duplicate account row makes the signed attribution ambiguous.
    if (seenUsers.has(m.userId)) {
      issues.push(
        `Crew member ${name} is listed twice on this JHA — keep exactly one entry per person (each person has one checklist and one signature). Remove the duplicate entry and try again.`
      );
      continue;
    }
    seenUsers.set(m.userId, name);
    const sig = typeof m.signature === 'string' ? m.signature.trim() : '';
    if (sig === '') {
      issues.push(`Sign-off missing: ${name} has not signed. ${HOW_TO_FIX}`);
    }
    const items = lookup(m.userId);
    const checks: JhaPpeCheck[] = Array.isArray(m.ppeChecks) ? m.ppeChecks : [];

    // Pass 0 — one row per target (per ITEM for item rows, per gear for
    // legacy type-level rows) + row resolution.
    const byTarget = new Map<string, { check: JhaPpeCheck; gear: JhaPpeGearDef }>();
    for (const check of checks) {
      if (!check || typeof check !== 'object') continue;
      const gear = JHA_PPE_GEAR.find((g) => g.id === check.gearId);
      if (!gear) continue;
      const targetKey = check.assetId != null ? `asset:${check.assetId}` : `type:${gear.id}`;
      if (byTarget.has(targetKey)) {
        issues.push(
          check.assetId != null
            ? `PPE check-off invalid: ${name}'s ${gear.label} item is listed twice. Keep exactly one check-off per item — remove the duplicate so the record is unambiguous. ${HOW_TO_FIX}`
            : `PPE check-off invalid: ${name}'s ${gear.label} inspection is listed twice. Keep exactly one row per gear — remove the duplicate so the record is unambiguous. ${HOW_TO_FIX}`
        );
        continue;
      }
      byTarget.set(targetKey, { check, gear });
    }

    // Pass — resolution: every present row fully checked, or "Not used today"
    // (a spare piece that stays in the truck — "inspected before use"). The
    // per-type rule below still demands the IN-USE piece be checked.
    for (const { check, gear } of byTarget.values()) {
      const who = check.assetId != null ? ` for ${itemIdentity({ assetId: check.assetId, ...(items?.find((i) => i.assetId === check.assetId) ?? {}) })}` : '';
      if (check.notUsed === true) continue;
      const got = checkedCount(check);
      if (!rowComplete(check, gear)) {
        issues.push(
          gear.required
            ? `PPE check-off incomplete: ${name} has not checked off the ${gear.label} inspection (${got} of ${gear.points.length} items)${who}. ${HOW_TO_FIX}`
            : `PPE check-off incomplete: ${name} started the ${gear.label} inspection but only checked ${got} of ${gear.points.length} items${who}. Either check off every item for that gear, or mark it "Not used today". ${HOW_TO_FIX}`
        );
      }
    }

    // Pass — binding, mirroring the server binder's passes 1-2: item rows
    // claim their item; legacy type-level rows auto-bind ONLY when exactly
    // one matching item is unclaimed (2+ = ambiguous — one check-off counts
    // for ONE item).
    const claimed = new Set<number>();
    for (const { check } of byTarget.values()) {
      if (check.assetId != null) claimed.add(check.assetId);
    }
    if (items) {
      for (const { check, gear } of byTarget.values()) {
        if (check.assetId != null) continue;
        const candidates = items.filter((i) => i.assetId != null && i.gearId === gear.id && !claimed.has(i.assetId));
        if (candidates.length === 1) claimed.add(candidates[0].assetId!);
        else if (candidates.length >= 2) {
          issues.push(
            `PPE check-off ambiguous: ${name} has ${candidates.length} ${gear.label} items (${candidates.map(itemIdentity).join(', ')}) — check off each item separately (model + serial) so the inspection counts for only that item. ${HOW_TO_FIX}`
          );
        }
      }
    }

    // Pass — coverage. Asset mode: every TRACKED line item must be claimed
    // (check off EACH piece — a "Not used today" waiver also claims its item).
    // UAT-009: untracked generic gear (assetId == null) is OFFERED for
    // check-off but never FORCED — matching the server binder. The required
    // per-type minimum below applies only to types the person TRACKS.
    if (items) {
      for (const item of items) {
        if (item.assetId == null) continue; // untracked fallback: offered, never forced (UAT-009)
        const gear = JHA_PPE_GEAR.find((g) => g.id === item.gearId)!;
        if (!claimed.has(item.assetId)) {
          issues.push(`PPE check-off incomplete: ${name} has not checked off the ${gear.label} (${itemIdentity(item)}). ${HOW_TO_FIX}`);
        }
      }
    }
    for (const gear of JHA_PPE_GEAR) {
      if (!gear.required) continue;
      // UAT-009: forcing applies to a gear type only when the person actually
      // TRACKS an item of it (or when asset knowledge is unavailable — items
      // undefined → pure type-level mode, the legacy contract).
      if (items && !items.some((i) => i.assetId != null && i.gearId === gear.id)) continue;
      if (!checks.some((c) => c && c.gearId === gear.id && c.notUsed !== true)) {
        issues.push(`PPE check-off incomplete: ${name} has not checked off the ${gear.label} inspection (0 of ${gear.points.length} items). ${HOW_TO_FIX}`);
      }
    }
  }
  // UAT-009: the same category can trip TWO rules — identical messages are
  // shown ONCE, never duplicated in the error summary.
  return [...new Set(issues)];
}